<template>
	<el-form
		:model="model"
		:rules="rules"
		:id="id"
		:ref="refName"
		@submit.prevent
		:disabled="waiting"
		:label-position="labelPosition"
		:scroll-to-error="scrollToError"
	>
		<slot />

		<el-alert :title="errMsg" type="error" v-if="showErr && errMsg" :closable="false" />
		<el-alert :title="waitingMsg" type="info" v-if="waiting && waitingMsg" :closable="false" />
	</el-form>
</template>

<script>
import { texts } from '../utils/texts.ts';
import { useStore } from '../store'; // Pfad anpassen

export default {
	name: 'TheForm',
	data() {
		return {
			isFormTouched: false,
			initialLoad: true,
			watcherRemoves: [],
		};
	},
	props: {
		model: {
			type: Object,
			required: true,
		},
		rules: {
			type: Object,
			required: true,
		},
		id: {
			type: String,
			required: true,
		},
		waiting: {
			type: Boolean,
			default: false,
		},
		showErr: {
			type: Boolean,
			default: false,
		},
		errMsg: {
			type: String,
			default: '',
		},
		waitingMsg: {
			type: String,
			default: '',
		},
		submitFunc: {
			type: Function,
			default: null,
		},
		labelPosition: {
			type: String,
			default: 'top',
		},
		scrollToError: {
			type: Boolean,
			default: true,
		},
		persistForm: {
			type: Boolean,
			default: false,
		},
		formNamespace: {
			type: String,
			default: '',
		},
		autoSave: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		refName() {
			return this.id + 'Ref';
		},
		store() {
			return useStore();
		},
		fullFormId() {
			return this.formNamespace ? `${this.formNamespace}.${this.id}` : this.id;
		},
		savedFormData() {
			return this.store.getters.getFormData(this.fullFormId);
		},
	},
	methods: {
		focusSelect(el) {
			if (!el) return;

			el.click();
		},
		saveFormState() {
			if (!this.persistForm || (!this.isFormTouched && !this.initialLoad)) return;

			let dataToSave = JSON.parse(JSON.stringify(this.model));
			this.store.dispatch('saveFormData', {
				formId: this.fullFormId,
				data: dataToSave,
			});

			this.initialLoad = false;
		},
		loadFormState() {
			if (!this.persistForm) return;

			const savedData = this.savedFormData;
			if (savedData) {
				Object.keys(savedData).forEach((key) => {
					if (key in this.model) {
						this.model[key] = savedData[key];
					}
				});
				return true;
			}
			return false;
		},
		clearFormState() {
			if (!this.persistForm) return;

			this.store.dispatch('clearFormData', this.fullFormId);
			this.isFormTouched = false;
		},
		markFormTouched() {
			this.isFormTouched = true;
			if (this.autoSave) {
				this.saveFormState();
			}
		},
		resetForm() {
			const formRef = this.$refs[this.refName];
			if (formRef) {
				formRef.resetFields();
			}
			this.clearFormState();
		},
		validateAndSubmit() {
			const formRef = this.$refs[this.refName];
			if (formRef) {
				formRef.validate((valid) => {
					if (valid && this.submitFunc) {
						this.submitFunc();
					}
				});
			} else if (this.submitFunc) {
				this.submitFunc();
			}
		},
		setupWatchers() {
			this.removeWatchers();
		},
		removeWatchers() {
			this.watcherRemoves.forEach((unwatchFn) => unwatchFn());
			this.watcherRemoves = [];
		},
	},
	mounted() {
		this.loadFormState();
		this.setupWatchers();
		const thisForm = this.$refs[this.refName].$el;
		const submitButton = thisForm.querySelector('.button-submit');

		thisForm.addEventListener('keydown', (e) => {
			if (e.target.tagName.toLowerCase() == 'textarea') return;

			if (e.key === 'Enter') {
				if (submitButton) {
					submitButton.click();
				} else if (this.submitFunc) {
					this.submitFunc();
				}
			}
		});
	},
	watch: {
		model: {
			handler() {
				this.setupWatchers();
			},
			deep: true,
		},
	},
	beforeUnmount() {
		this.removeWatchers();
		if (this.persistForm) {
			this.saveFormState();
		}
	},
};
</script>
